import { useOrganization } from 'core/stores/hooks/useOrganization';
import { isNotNull } from 'core/utils/isNotNull';
import {
  CalendarMeetingTemplateStatus,
  useSearchCalendarMeetingsQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { useCallback, useEffect, useState } from 'react';

const PAGE_SIZE = 100;

export const useListActiveMeetingTemplates = () => {
  const organization = useOrganization();
  // Stops the page walk (and the loading spinner) if a page fails, rather
  // than retrying the same nextToken forever or spinning indefinitely.
  const [pageError, setPageError] = useState(false);

  const { data, fetchMore, updateQuery, loading, refetch } =
    useSearchCalendarMeetingsQuery({
      // so loading is true when refetching
      notifyOnNetworkStatusChange: true,
      variables: {
        filter: {
          and: [
            { organizationId: { eq: organization.id } },
            { status: { eq: CalendarMeetingTemplateStatus.Active } },
            { isSingleUse: { ne: true } },
            { isWorkflowManaged: { ne: true } }
          ]
        },
        organizationId: organization.id,
        limit: PAGE_SIZE
      }
    });

  const nextToken = data?.searchCalendarMeetingTemplates?.nextToken;
  const hasMore = !!nextToken && !pageError;

  // Walk every page so templates beyond the first PAGE_SIZE are surfaced.
  useEffect(() => {
    if (!hasMore) {
      return;
    }

    fetchMore({
      variables: { nextToken },
      updateQuery: (prev, { fetchMoreResult }) => {
        const nextPage = fetchMoreResult.searchCalendarMeetingTemplates;
        if (!nextPage) {
          return prev;
        }

        return {
          searchCalendarMeetingTemplates: {
            ...nextPage,
            items: [
              ...(prev.searchCalendarMeetingTemplates?.items ?? []),
              ...nextPage.items
            ]
          }
        };
      }
    }).catch(() => setPageError(true));
  }, [hasMore, nextToken, fetchMore]);

  const refetchAll = useCallback(() => {
    setPageError(false);
    return refetch();
  }, [refetch]);

  return {
    data: data?.searchCalendarMeetingTemplates?.items.filter(isNotNull) || [],
    updateQuery,
    loading: loading || hasMore,
    refetch: refetchAll
  };
};
